(ns arthur.ui.canvas
  "The one imperative sink.

  Everything below here is pure and returns bytes; this is where bytes become
  pixels, and it is the only namespace allowed to touch a canvas. domain/raster
  deliberately stops at `->rgba` returning plain bytes — an ImageData is a DOM
  type, and keeping it out of domain/ is what lets every rasteriser assertion run
  under node.

  THE CANVAS IS THE RASTER'S OWN SIZE and is scaled up by CSS with
  `image-rendering: pixelated`, rather than by expanding in `->rgba`. Two reasons:
  a 2x expansion in JS is four times the bytes to write per frame for a result
  the GPU gives away, and nearest-neighbour is then the browser's guarantee rather
  than something this code has to keep being right about. A browser that smoothed
  the upscale would misrepresent the exact thing the preview exists to judge, so
  the CSS rule is load-bearing and lives in the host page next to the canvas."
  (:require [arthur.domain.palette :as pal]
            [arthur.domain.raster :as raster]))

(defonce ^:private cache
  ;; canvas element -> its ImageData, so a frame writes into the array the
  ;; canvas already owns instead of allocating a quarter of a megabyte.
  (atom {}))

(defn- image-data-for [^js ctx ^js el w h]
  (let [have (get @cache el)]
    (if (and have (= w (.-width have)) (= h (.-height have)))
      have
      (let [img (.createImageData ctx w h)]
        (swap! cache assoc el img)
        img))))

(defn- pasteboard!
  "Clear every pixel of `data` off a stage `sw` by `sh` that nothing drew on,
  so the work area shows through round what is drawn there. Never into the
  stage's own span of a row, which is always the picture."
  [^js data {:keys [w h ^js cov] [ox oy] :origin} [sw sh]]
  (let [px (js/Uint32Array. (.-buffer data) (.-byteOffset data) (* w h))
        x0 (- ox) y0 (- oy) x1 (+ x0 sw) y1 (+ y0 sh)
        clear! (fn [from to]
                 (loop [o from]
                   (when (< o to)
                     (when (zero? (aget cov o)) (aset px o 0))
                     (recur (inc o)))))]
    (dotimes [y h]
      (let [row (* y w)]
        (if (and (<= y0 y) (< y y1))
          (do (clear! row (+ row (min w (max 0 x0)))) (clear! (+ row (min w (max 0 x1))) (+ row w)))
          (clear! row (+ row w)))))))

(defn place!
  "Show canvas `el` over `[x0 y0 x1 y1]` of the work area round a stage `[sw sh]`,
  in stage pixels: in proportion to the stage, so zooming does not move it."
  [^js el [x0 y0 x1 y1] [sw sh]]
  (let [style (.-style el) pc #(str (/ (* 100 %1) %2) "%")]
    (set! (.-display style) "")
    (set! (.-left style) (pc x0 sw)) (set! (.-top style) (pc y0 sh))
    (set! (.-width style) (pc (- x1 x0) sw)) (set! (.-height style) (pc (- y1 y0) sh))))

(defn blit!
  "Expand an indexed raster through the palette and put it on the canvas.

  With `stage`, `[w h]`, the raster is some part of the work area round a stage
  that size — its `:origin` says which — and the canvas is placed over that
  part, in proportion to the stage, so zooming does not move it."
  ([el r] (blit! el r pal/rgb))
  ([el r palette-rgb] (blit! el r palette-rgb nil))
  ([^js el {:keys [w h cov] :as r} palette-rgb stage]
   (when el
     ;; Guarded: assigning width reallocates the backing store, so doing it
     ;; unconditionally would throw a canvas away thirty times a second.
     (when (not= w (.-width el)) (set! (.-width el) w))
     (when (not= h (.-height el)) (set! (.-height el) h))
     (let [ctx (.getContext el "2d")
           img (image-data-for ctx el w h)]
       (raster/->rgba r palette-rgb 1 (.-data img))
       (when cov (pasteboard! (.-data img) r stage))
       (.putImageData ctx img 0 0))
     (when stage
       (let [[ox oy] (:origin r)]
         (place! el [ox oy (+ ox w) (+ oy h)] stage))))))

(defn hide!
  "Show nothing on canvas `el`: the part of the work area on screen has nothing
  in it."
  [^js el]
  (set! (.. el -style -display) "none"))

(defn ->png
  "An indexed raster as a PNG data URL, expanded through `palette-rgb`.

  For a THUMBNAIL, which is the one picture in this app that is not the preview.
  Two consequences, and both are departures from the rule at the top of this
  namespace:

  An `<img>` rather than the canvas itself, because a pool of them is a list that
  rebuilds whenever anything in the document changes, and a data URL is a value
  the caller can cache against the document it was drawn from — a canvas is an
  element that has to be found and repainted.

  And therefore SMOOTHLY downscaled, because `image-rendering: pixelated` is a
  rule about canvases. At a tenth of the stage's size nearest neighbour samples
  one pixel in a hundred, and a drawing made of flat shapes a few pixels across
  reduces to speckle; the silhouette is what makes a thumbnail recognisable, and
  smoothing is what keeps it. The pixelated rule is load-bearing for the preview
  that JUDGES the output. This is a picture to tell one name from another by."
  [{:keys [w h] :as r} palette-rgb]
  (let [el (js/document.createElement "canvas")]
    (set! (.-width el) w)
    (set! (.-height el) h)
    ;; Deliberately not through `image-data-for`: that cache is keyed by element
    ;; and this element is thrown away, so every thumbnail would leave a quarter
    ;; of a megabyte in it that nothing can ever find again.
    (let [ctx (.getContext el "2d")
          img (.createImageData ctx w h)]
      (raster/->rgba r palette-rgb 1 (.-data img))
      (.putImageData ctx img 0 0))
    (.toDataURL el "image/png")))


(defonce ^:private onion-surface (atom nil))

(defn clear-overlay! [el]
  (when el
    (.clearRect (.getContext el "2d") 0 0 (.-width el) (.-height el))))

(defn tint-layer!
  "Composite a covered raster in one editor tint, keeping uncovered pixels clear,
  with its first pixel at `[x y]` on `el`."
  [el {:keys [w h cov]} [x y] [red green blue] opacity]
  (when el
    (let [scratch (or @onion-surface
                      (reset! onion-surface (js/document.createElement "canvas")))
          _ (when (not= w (.-width scratch)) (set! (.-width scratch) w))
          _ (when (not= h (.-height scratch)) (set! (.-height scratch) h))
          ctx (.getContext scratch "2d")
          img (image-data-for ctx scratch w h)
          data (.-data img)]
      (dotimes [i (* w h)]
        (let [p (* i 4)]
          (aset data p red)
          (aset data (+ p 1) green)
          (aset data (+ p 2) blue)
          (aset data (+ p 3) (if (pos? (aget cov i)) (js/Math.round (* 255 opacity)) 0))))
      (.putImageData ctx img 0 0)
      (.drawImage (.getContext el "2d") scratch x y))))
